import React from 'react';
import { Text, TouchableOpacity, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { useTheme, TYPOGRAPHY, withWeight } from '../../theme';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER } from '../../utils/webStyles';

interface TextLinkProps {
  label: string;
  onPress: () => void;
  /** Defaults to colors.text (ink). Pass a semantic color only when the
   *  link IS the semantic — category words, risk actions. */
  color?: string;
  style?: ViewStyle;
  textStyle?: TextStyle;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  testID?: string;
  hitSlop?: number;
}

/**
 * Inline text link: ink label, hover underline + pointer cursor on web,
 * plain pressable text on native. Use for "Sign in", "View all", "Forgot
 * password?" style affordances instead of ad-hoc Pressable+Text.
 *
 * Ink, not pine: in the Plaud grammar green is a CATEGORY signal
 * (progress/readiness), so a green link reads as a category word. Actions
 * and links are ink; the underline on hover carries the affordance.
 */
export function TextLink({
  label,
  onPress,
  color,
  style,
  textStyle,
  accessibilityLabel,
  accessibilityHint,
  testID,
  hitSlop,
}: TextLinkProps) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint}
      testID={testID}
      hitSlop={hitSlop}
      {...hoverProps}
      style={[CURSOR_POINTER, style]}>
      <Text
        style={[
          styles.text,
          { color: color ?? colors.text },
          hovered && styles.underline,
          textStyle,
        ]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  text: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  underline: {
    textDecorationLine: 'underline',
  },
});
